/* =============================================================================
   T3BLOGG_OS — assets/quickview.js
   Intercepts clicks on .file-card and opens the product in a new draggable
   terminal-window over the desktop, instead of navigating to /products/<handle>.
   Falls back to normal navigation if the fetch fails.
   ============================================================================= */

(function () {
  'use strict';
  const $ = (s, r = document) => r.querySelector(s);

  const fmt = cents => window.T3Money(cents);

  function imgAt(url, w) {
    if (!url) return '';
    // Shopify CDN supports ?width=N as a transform.
    const sep = url.includes('?') ? '&' : '?';
    return url + sep + 'width=' + w;
  }

  function escHTML(s) {
    return String(s || '').replace(/[&<>"']/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  async function fetchProduct(handle) {
    const r = await fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('product fetch failed');
    return r.json();
  }

  // Pick the first option that has more than one value across variants.
  // Robust against products that use any option name (Size, Storrelse, Color, etc).
  function findPickerIdx(p) {
    const opts = p.options || [];
    if (!p.variants || p.variants.length < 1) return -1;
    for (let i = 0; i < opts.length; i++) {
      const seen = new Set();
      for (const v of p.variants) seen.add(v.options[i]);
      if (seen.size > 1) return i;
    }
    return -1;
  }
  function optionName(p, idx) {
    const o = (p.options || [])[idx];
    return typeof o === 'string' ? o : ((o && o.name) || 'OPTION');
  }

  function buildBody(p) {
    const filename = (p.handle || '').toUpperCase().replace(/-/g, '_') + '.JPG';
    const pickerIdx = findPickerIdx(p);
    const pickerName = pickerIdx >= 0 ? optionName(p, pickerIdx) : '';
    const current = p.variants.find(v => v.available) || p.variants[0];

    // Media: vertically scrollable column with all images stacked + thumbnail strip nav.
    const hasImgs = p.images && p.images.length > 0;
    const mediaScrollHTML = hasImgs
      ? `<div class="media-scroll" data-media-scroll>${p.images.map((src, i) => `
          <img src="${imgAt(src, 1200)}" alt="${escHTML(p.title)}" loading="${i === 0 ? 'eager' : 'lazy'}" data-idx="${i}">
        `).join('')}</div>`
      : `<div class="media-scroll"><span class="ph">// PLACEHOLDER<br>${filename}</span></div>`;
    const thumbsHTML = hasImgs && p.images.length > 1
      ? `<div class="media-thumbs" data-media-thumbs>${p.images.map((src, i) => `
          <button type="button" data-idx="${i}" class="${i === 0 ? 'active' : ''}" aria-label="Image ${i + 1}">
            <img src="${imgAt(src, 200)}" alt="">
          </button>`).join('')}</div>`
      : '';

    // Variant pills (uses whatever the first multi-value option is)
    let sizesHTML = '';
    if (pickerIdx >= 0) {
      const seen = new Set();
      const values = [];
      p.variants.forEach(v => {
        const val = v.options[pickerIdx];
        if (!seen.has(val)) { seen.add(val); values.push(val); }
      });
      sizesHTML = `
        <div>
          <div class="var-label">// ${escHTML(pickerName.toUpperCase())}</div>
          <div class="sizes" role="radiogroup" aria-label="${escHTML(pickerName)}">
            ${values.map(value => {
              const variant = p.variants.find(v => v.options[pickerIdx] === value && v.available)
                          || p.variants.find(v => v.options[pickerIdx] === value);
              const isAvail = variant && variant.available;
              const isSel = current && current.options[pickerIdx] === value;
              return `<button type="button" class="size-pill ${!isAvail ? 'sold-out' : ''}"
                              data-size="${escHTML(value)}"
                              aria-pressed="${isSel ? 'true' : 'false'}"
                              ${!isAvail ? 'disabled' : ''}>${escHTML(value)}</button>`;
            }).join('')}
          </div>
        </div>`;
    }

    return `
      <div class="pd">
        <div class="media">
          ${mediaScrollHTML}
          ${thumbsHTML}
        </div>

        <div class="info">
          <div class="fn">${filename}</div>
          <h1 class="title">${escHTML(p.title)}</h1>
          <div class="price-row">
            <span data-price>${fmt(current ? current.price : p.price)}</span>
            <span class="sm">// inc. mva</span>
            ${current && current.compare_at_price && current.compare_at_price > current.price
              ? `<span class="compare" data-compare>${fmt(current.compare_at_price)}</span>`
              : `<span class="compare" data-compare hidden></span>`}
          </div>
          <div class="desc">${p.description || ''}</div>

          ${sizesHTML}

          <form action="/cart/add" method="post" enctype="multipart/form-data" novalidate data-product-form data-filename="${filename}">
            <input type="hidden" name="id" value="${current ? current.id : ''}">
            <div class="add">
              <div class="qty">
                <button type="button" class="dec" aria-label="decrease">-</button>
                <input type="number" name="quantity" value="1" min="1" max="99" inputmode="numeric">
                <button type="button" class="inc" aria-label="increase">+</button>
              </div>
              <button type="submit" class="btn-add" ${current && current.available ? '' : 'disabled'}>
                ${current && current.available ? '&gt;&gt; ADD TO CART' : '// SOLD_OUT'}
              </button>
            </div>
          </form>
        </div>
      </div>`;
  }

  function bindProductWindow(win, p) {
    const pickerIdx = findPickerIdx(p);
    const sizesEl = win.querySelector('.sizes');
    const idInput = win.querySelector('input[name="id"]');
    const priceEl = win.querySelector('[data-price]');
    const compareEl = win.querySelector('[data-compare]');
    const btn = win.querySelector('.btn-add');
    const qtyEl = win.querySelector('input[name="quantity"]');

    let selectedVariant = p.variants.find(v => v.available) || p.variants[0];

    function paint() {
      if (!selectedVariant) return;
      if (idInput) idInput.value = selectedVariant.id;
      if (priceEl) priceEl.textContent = fmt(selectedVariant.price);
      if (compareEl) {
        if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
          compareEl.textContent = fmt(selectedVariant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (btn) {
        btn.disabled = !selectedVariant.available;
        btn.innerHTML = selectedVariant.available ? '&gt;&gt; ADD TO CART' : '// SOLD_OUT';
      }
    }

    if (sizesEl && pickerIdx >= 0) {
      sizesEl.addEventListener('click', e => {
        const pill = e.target.closest('.size-pill');
        if (!pill || pill.disabled) return;
        sizesEl.querySelectorAll('.size-pill').forEach(p => p.setAttribute('aria-pressed', 'false'));
        pill.setAttribute('aria-pressed', 'true');
        const variant = p.variants.find(v => v.options[pickerIdx] === pill.dataset.size && v.available)
                     || p.variants.find(v => v.options[pickerIdx] === pill.dataset.size);
        if (variant) {
          selectedVariant = variant;
          paint();
        }
      });
    }

    const dec = win.querySelector('.qty .dec');
    const inc = win.querySelector('.qty .inc');
    if (dec) dec.addEventListener('click', e => { e.preventDefault(); qtyEl.value = Math.max(1, parseInt(qtyEl.value || 1, 10) - 1); });
    if (inc) inc.addEventListener('click', e => { e.preventDefault(); qtyEl.value = Math.min(99, parseInt(qtyEl.value || 1, 10) + 1); });

    // Thumbnail strip: click → scroll main media to that image. Scroll → update active thumb.
    const scroller = win.querySelector('[data-media-scroll]');
    const thumbs   = win.querySelectorAll('[data-media-thumbs] button');
    const scrollImgs = scroller ? scroller.querySelectorAll('img[data-idx]') : [];

    if (thumbs.length && scroller) {
      thumbs.forEach(btn => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.idx, 10);
          const target = scrollImgs[idx];
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
              const idx = parseInt(entry.target.dataset.idx, 10);
              thumbs.forEach(b => b.classList.toggle('active', parseInt(b.dataset.idx, 10) === idx));
            }
          });
        }, { root: scroller, threshold: [0.5] });
        scrollImgs.forEach(img => io.observe(img));
      }
    }

    paint();
  }

  // Pending "restore grid" closure — set while a product is being viewed inside
  // the shop window, called on Back button or browser back.
  let shopRestore = null;

  async function showProductInShop(handle) {
    const shopWin = document.getElementById('win-shop-grid');
    if (!shopWin) {
      // No shop window present (e.g. a collection page) → go to the standalone product page.
      window.location.href = '/products/' + handle;
      return;
    }
    const body    = shopWin.querySelector(':scope > .body');
    const titleEl = shopWin.querySelector(':scope > .bar .title');
    const pathEl  = shopWin.querySelector(':scope > .path');
    if (!body) return;

    // Snapshot the current grid view so we can return to it.
    const gridSnapshot  = body.innerHTML;
    const titleSnapshot = titleEl ? titleEl.textContent : '';
    const pathSnapshot  = pathEl ? pathEl.textContent : '';

    body.innerHTML = `
      <div class="shop-loader" role="status" aria-live="polite">
        <div class="shop-loader-bar"><div class="fill"></div></div>
        <div class="shop-loader-text">&gt; loading ${escHTML(handle)}<span class="cur">_</span></div>
      </div>`;

    let product;
    try {
      product = await fetchProduct(handle);
    } catch (err) {
      body.innerHTML = gridSnapshot;
      window.location.href = '/products/' + handle;
      return;
    }

    body.innerHTML = `
      <div class="pd-wrap">
        <div class="pd-nav"><button type="button" class="pd-back">&lt; BACK TO STORE</button></div>
        ${buildBody(product)}
      </div>`;
    bindProductWindow(body, product);

    if (titleEl) titleEl.textContent = '~/shop/' + handle;
    if (pathEl)  pathEl.textContent  = 'root@t3blogg:~/shop/' + handle + ' $ cat info.json';
    body.scrollTop = 0;

    function restoreGrid() {
      body.innerHTML = gridSnapshot;
      if (titleEl) titleEl.textContent = titleSnapshot;
      if (pathEl)  pathEl.textContent  = pathSnapshot;
      shopRestore = null;
    }
    shopRestore = restoreGrid;

    const backBtn = body.querySelector('.pd-back');
    if (backBtn) backBtn.addEventListener('click', () => {
      restoreGrid();
      try { if (history.state && history.state.t3 === 'product') history.replaceState({}, '', '/'); } catch (e) {}
    });

    // Shareable URL — reloading it renders the standalone product page.
    try {
      history.pushState({ t3: 'product', handle: handle }, '', '/products/' + encodeURIComponent(handle));
    } catch (e) {}

    document.dispatchEvent(new CustomEvent('syslog', { detail: { text: `OPENED ${handle}` } }));
  }

  // Browser back/forward while a product is open → restore the grid.
  window.addEventListener('popstate', () => {
    if (shopRestore) shopRestore();
  });

  // Intercept product-card clicks INSIDE the shop window → render in place.
  // Cards elsewhere (collection page) keep their normal navigation.
  document.addEventListener('click', e => {
    const card = e.target.closest('.file-card');
    if (!card) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    // Mobile + tablet: let the card's <a href="/products/…"> navigate to the
    // standalone page (cleaner UX, fewer bugs — the in-window 2-col layout traps
    // the add-to-cart off-screen on touch). Desktop renders detail in the window.
    if (matchMedia('(max-width: 1024px)').matches) return;
    if (!card.closest('#win-shop-grid')) return;
    const handle = card.dataset.handle;
    if (!handle) return;
    e.preventDefault();
    showProductInShop(handle);
  });

  window.T3Quickview = { open: showProductInShop };
})();
